import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './styles.css';
import { CartProvider } from './context/CartContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './utils/ScrollToTop';
import InstagramFeed from './components/InstagramFeed';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', fontFamily: 'Urbanist, sans-serif' }}>
          <p style={{ color: '#888', fontSize: '15px' }}>Something went wrong loading this page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 24px', border: '1px solid #111', background: 'none', cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit' }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const Category = lazy(() => import('./pages/Category'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const About = lazy(() => import('./pages/About'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const Shipping = lazy(() => import('./pages/Shipping'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Faq = lazy(() => import('./pages/Faq'));

function AppShell() {
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const isProductDetailPage = pathname.startsWith('/product/');
  const showInstagramFeed = pathname === '/' || pathname === '/home';

  return (
    <>
      <Navbar />
      <div className="app-shell">
        <ScrollToTop />
        <ErrorBoundary key={location.key}>
        <Suspense fallback={<div className="page-loading-placeholder" aria-hidden="true" />}>
        <div
          className={`page-transition${isProductDetailPage ? ' page-transition--fixed-ui' : ''}`}
        >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:collection" element={<Shop />} />
          <Route path="/dogs" element={<Category category="dogs" />} />
          <Route path="/dogs/:subcategory" element={<Category category="dogs" />} />
          <Route path="/cats" element={<Category category="cats" />} />
          <Route path="/cats/:subcategory" element={<Category category="cats" />} />
          <Route path="/small-animals" element={<Category category="small-animals" />} />
          <Route path="/small-animals/:subcategory" element={<Category category="small-animals" />} />
          <Route path="/health-and-wellness" element={<Category category="health-and-wellness" />} />
          <Route path="/product/:handle" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/about-us" element={<Navigate to="/about" replace />} />
          <Route path="/who-are-we" element={<Navigate to="/about" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </div>
        </Suspense>
        </ErrorBoundary>
        {showInstagramFeed ? <InstagramFeed /> : null}
        <Footer />
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <CartProvider>
        <AppShell />
      </CartProvider>
    </BrowserRouter>
  );
}
